import { type SelectProps, Select } from 'antd5';
import { type BaseSelectRef } from 'rc-select';
import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type Ref,
  forwardRef,
  useCallback,
  useRef,
  useState,
} from 'react';

import { OptionsSlot, TableFilterMultiSelectRoot } from './TableFilterMultiSelect.style';

export type TableFilterMultiSelectProps = Omit<SelectProps, 'getPopupContainer' | 'mode'> & {
  /** Max height (px) for the in-panel options list. Default 200. */
  listHeight?: number;
  /**
   * When true (default), keep the search query after selecting an option so
   * users can bulk-select from a filtered list — same behavior as CollaboratorChooser.
   * Cleared when the options panel closes (unless search is controlled by the parent).
   */
  persistSearch?: boolean;
};

/**
 * Call from OK/Reset `onMouseDown`. Runs the action immediately and stops the
 * Select from treating the press as an outside-click.
 *
 * Without this, an open in-panel options list closes on mousedown, the footer
 * shifts up, and the subsequent `click` misses the button — so OK needs two presses.
 */
export const handleTableFilterActionMouseDown = (
  event: ReactMouseEvent,
  action: () => void
): void => {
  event.preventDefault();
  event.stopPropagation();
  action();
};

/**
 * Multiselect for Ant Design table `filterDropdown`s.
 *
 * Renders the options panel into a slot *below the input and above the filter
 * footer* (OK / Reset), so the list expands the filter layout instead of
 * covering the action buttons.
 *
 * Sketch / shared wrapper for the in-panel portal approach (#4). Adopt in
 * SiteFilter, SimpleAssetFilter, AssetAttributeSelectFilter, etc.
 */
export const TableFilterMultiSelect = forwardRef(function TableFilterMultiSelect(
  {
    listHeight = 200,
    persistSearch = true,
    autoClearSearchValue,
    searchValue: searchValueProp,
    onSearch,
    onOpenChange,
    style,
    ...props
  }: TableFilterMultiSelectProps,
  ref: Ref<BaseSelectRef>
) {
  const optionsSlotRef = useRef<HTMLDivElement>(null);
  const [internalSearchValue, setInternalSearchValue] = useState('');

  const isSearchControlled = searchValueProp !== undefined;
  const searchValue = isSearchControlled ? searchValueProp : internalSearchValue;

  const handleSearch = useCallback(
    (value: string) => {
      if (!isSearchControlled) {
        setInternalSearchValue(value);
      }
      onSearch?.(value);
    },
    [isSearchControlled, onSearch]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && persistSearch && !isSearchControlled) {
        setInternalSearchValue('');
      }
      onOpenChange?.(open);
    },
    [isSearchControlled, onOpenChange, persistSearch]
  );

  const getPopupContainer = useCallback(() => optionsSlotRef.current ?? document.body, []);

  const rootStyle = {
    ...style,
    ['--table-filter-multi-select-list-height' as string]: `${listHeight}px`,
  } as CSSProperties;

  return (
    <TableFilterMultiSelectRoot style={rootStyle}>
      <Select
        {...props}
        ref={ref}
        mode="multiple"
        listHeight={listHeight}
        getPopupContainer={getPopupContainer}
        autoClearSearchValue={persistSearch ? false : autoClearSearchValue}
        searchValue={persistSearch ? searchValue : searchValueProp}
        onSearch={persistSearch ? handleSearch : onSearch}
        onOpenChange={persistSearch ? handleOpenChange : onOpenChange}
      />
      {/*
        Prevent mousedown from blurring the Select input when choosing options.
        Without this, the popup can close before the option click registers, so
        onChange never fires and OK applies an empty filter.
      */}
      <OptionsSlot ref={optionsSlotRef} onMouseDown={event => event.preventDefault()} />
    </TableFilterMultiSelectRoot>
  );
});
